"use client";

import React, { useMemo, useState, useEffect } from "react";
import { BadgePercent, Edit3Icon, Plus, Trash2, X } from "lucide-react";
import { createPortal } from "react-dom";
import { useMandals } from "../context/MandalsContext";
import { useDistricts } from "../context/DistrictsContext";

export default function Mandals() {
  const {
    mandals,
    fetchMandals,
    createMandal,
    updateMandal,
    deleteMandal,
    updateMandalStatus,
  } = useMandals();

  const { districts, fetchDistricts } = useDistricts(); // ✅ FIX 1

  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
const [status, setStatus] = useState("active");
  /* ---------------- LOAD ---------------- */
  useEffect(() => {
    fetchMandals();
    fetchDistricts(); // ✅ FIX 2 (THIS WAS MISSING)
  }, []);

  useEffect(() => {
    fetchMandals(selectedDistrict);
  }, [selectedDistrict]);

  /* ---------------- FILTER ---------------- */
  const filtered = useMemo(() => {
    let data = [...mandals].filter((m) =>
      m.name.toLowerCase().includes(search.toLowerCase())
    );

    if (sortBy === "az") data.sort((a, b) => a.name.localeCompare(b.name));
    if (sortBy === "za") data.sort((a, b) => b.name.localeCompare(a.name));

    return data;
  }, [mandals, search, sortBy]);

  /* ---------------- SAVE ---------------- */
  const save = async () => {
    if (!name.trim() || !selectedDistrict) return;

    if (editing) {
      await updateMandal(editing.id, {
        name,
        district_id: selectedDistrict,
      });
    } else {
      await createMandal({
        name,
        district_id: selectedDistrict,
      });
    }

    setModalOpen(false);
    setEditing(null);
    setName("");
  };

  const toggleStatusHandler = (item) => {
    const newStatus =
      item.status === "active" ? "inactive" : "active";

    updateMandalStatus(item.id, newStatus);
  };

  const removeHandler = (id) => {
    if (window.confirm("Are you sure you want to delete this mandal?")) {
      deleteMandal(id);
    }
  };

  const td = "px-6 py-4 text-center align-middle";

  return (
    <div className="p-4 md:p-6 space-y-6">

      {/* HEADER (UNCHANGED) */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-teal-700 rounded-xl flex items-center justify-center shadow-sm">
            <BadgePercent className="w-6 h-6 text-white" />
          </div>

          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-teal-800">
              Mandals
            </h1>
            <p className="text-gray-600 mt-1">
              Manage your mandals
            </p>
          </div>
        </div>

        <button
          onClick={() => setModalOpen(true)}
          className="w-full sm:w-auto flex items-center justify-center gap-2 bg-teal-600 text-white px-5 py-2.5 sm:px-6 sm:py-3 rounded-xl hover:bg-teal-700"
        >
          <Plus className="w-5 h-5" />
          Add Mandal
        </button>
      </div>

      {/* STATS (UNCHANGED) */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white border border-orange-100 rounded-xl p-4">
          <p className="text-xs text-gray-500">Total</p>
          <p className="text-2xl font-bold">{mandals.length}</p>
        </div>

        <div className="bg-white border border-orange-100 rounded-xl p-4">
          <p className="text-xs text-gray-500">Active</p>
          <p className="text-2xl font-bold text-green-600">
            {mandals.filter((m) => m.status === "active").length}
          </p>
        </div>

        <div className="bg-white border border-orange-100 rounded-xl p-4">
          <p className="text-xs text-gray-500">Inactive</p>
          <p className="text-2xl font-bold text-red-600">
            {mandals.filter((m) => m.status === "inactive").length}
          </p>
        </div>
      </div>

      {/* FILTERS (UNCHANGED) */}
      <div className="flex flex-col md:flex-row gap-4 bg-orange-50 border border-orange-200 rounded-xl p-4">

        <input
          placeholder="Search mandal..."
          className="px-4 py-2 border rounded-xl w-full md:w-80"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select
          className="px-4 py-2 border rounded-xl w-full md:w-52"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
        >
          <option value="">Sort By</option>
          <option value="az">A → Z</option>
          <option value="za">Z → A</option>
        </select>
      </div>

      {/* TABLE (UNCHANGED) */}
      <div className="hidden md:block bg-white rounded-xl shadow-sm border overflow-hidden">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className={td}>ID</th>
              <th className={td}>District</th>
              <th className={td}>Mandal</th>
              <th className={td}>Status</th>
              <th className="px-6 py-4 text-right">Actions</th>
            </tr>
          </thead>

          <tbody>
            {filtered.map((item) => (
              <tr key={item.id} className="border-t hover:bg-gray-50">
                <td className={td}>{item.id}</td>
                <td className={td}>{item.district_name}</td>
                <td className={`${td} font-medium`}>{item.name}</td>

                <td className={td}>
                  <div className="flex justify-center items-center">
                    <button
                      onClick={() => toggleStatusHandler(item)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full ${
                        item.status === "active"
                          ? "bg-green-600"
                          : "bg-gray-300"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform bg-white rounded-full ${
                          item.status === "active"
                            ? "translate-x-6"
                            : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>
                </td>

                <td className="px-6 py-4">
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => {
                        setEditing(item);
                        setName(item.name);
                        setSelectedDistrict(item.district_id);
                        setModalOpen(true);
                      }}
                      className="text-teal-600"
                    >
                      <Edit3Icon size={16} />
                    </button>

                    <button
                      onClick={() => removeHandler(item.id)}
                      className="text-red-500"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* MODAL (UNCHANGED EXCEPT NOW WORKS) */}
     {modalOpen &&
  createPortal(
    <div className="fixed inset-0 z-50 flex justify-end">

      {/* BACKDROP */}
      <div
        className="absolute inset-0 bg-black/60"
        onClick={() => setModalOpen(false)}
      />

      {/* RIGHT PANEL */}
      <div className="relative w-full max-w-md h-full bg-[#f3e7db] rounded-l-3xl shadow-2xl overflow-y-auto">

        {/* HEADER */}
        <div className="relative p-6 border-b border-orange-200 text-center">
          <button
            onClick={() => setModalOpen(false)}
            className="absolute top-4 left-4 w-9 h-9 bg-black text-white rounded-full flex items-center justify-center"
          >
            <X size={16} />
          </button>

          <h2 className="text-2xl font-bold text-teal-700">
            {editing ? "Edit Mandal" : "Add New Mandal"}
          </h2>
          <div className="w-12 h-[3px] bg-orange-400 mx-auto mt-2 rounded-full" />
        </div>

        {/* FORM */}
        <div className="p-6">
          <div className="bg-[#e6d5c3] rounded-2xl p-5 space-y-5">

            {/* DISTRICT */}
            <div>
              <label className="text-sm font-medium flex items-center gap-2 mb-2">
                <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                District *
              </label>

              <select
                value={selectedDistrict}
                onChange={(e) => setSelectedDistrict(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-orange-200 bg-white outline-none"
              >
                <option value="">Select district</option>
                {districts.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>

            {/* MANDAL NAME */}
            <div>
              <label className="text-sm font-medium flex items-center gap-2 mb-2">
                <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                Mandal Name *
              </label>

              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter mandal name..."
                maxLength={50}
                className="w-full px-4 py-3 rounded-xl border border-orange-200 outline-none"
              />

              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>Keep it short and clear</span>
                <span>{name.length}/50</span>
              </div>
            </div>

            {/* STATUS */}
            <div>
              <label className="text-sm font-medium flex items-center gap-2 mb-2">
                <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                Status
              </label>

              <div className="flex gap-4">
                <button
                  onClick={() => setStatus("active")}
                  className={`px-4 py-2 rounded-full text-sm ${
                    status === "active"
                      ? "bg-green-100 text-green-700"
                      : "bg-gray-100"
                  }`}
                >
                  Active
                </button>

                <button
                  onClick={() => setStatus("inactive")}
                  className={`px-4 py-2 rounded-full text-sm ${
                    status === "inactive"
                      ? "bg-red-100 text-red-700"
                      : "bg-gray-100"
                  }`}
                >
                  Inactive
                </button>
              </div>
            </div>

          </div>

          {/* ACTION BUTTONS */}
          <div className="flex gap-4 mt-6">
            <button
              onClick={() => setModalOpen(false)}
              className="flex-1 py-3 rounded-xl border bg-white text-gray-600"
            >
              Cancel
            </button>

            <button
              onClick={save}
              className="flex-1 py-3 rounded-xl bg-teal-600 text-white font-semibold"
            >
              Save
            </button>
          </div>
        </div>

      </div>
    </div>,
    document.body
  )}
    </div>
  );
}